<!-- Admin Layout with Sidebar and Top Navigation -->
<div class="min-h-screen bg-gradient-to-br from-slate-50 via-green-50 to-emerald-50 flex">
  
  <!-- Sidebar -->
  <aside 
    [class.translate-x-0]="sidebarOpen"
    [class.-translate-x-full]="!sidebarOpen"
    class="fixed lg:static inset-y-0 left-0 z-50 w-72 bg-white border-r border-slate-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 shadow-xl lg:shadow-none">
    
    <!-- Sidebar Header -->
    <div class="h-20 flex items-center justify-between px-6 border-b border-slate-200 bg-gradient-to-r from-green-600 to-emerald-600">
      <div class="flex items-center gap-3">
        <!-- App Logo -->
        <div class="w-11 h-11 bg-white rounded-xl flex items-center justify-center shadow-lg p-1.5">
          <img src="assets/logo.svg" alt="IT Support Chatbot Logo" class="w-full h-full object-contain">
        </div>
        <div class="text-white">
          <h1 class="text-lg font-bold">JoyBot MHealth</h1>
          <p class="text-xs text-green-100">Admin Panel</p>
        </div>
      </div>
      <button (click)="toggleSidebar()" class="lg:hidden text-white hover:bg-white/10 rounded-lg p-2 transition-colors">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    </div>

    <!-- User Profile -->
    <div class="p-6 border-b border-slate-200">
      <div class="flex items-center gap-3">
        <div class="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
          {{ user?.username?.charAt(0).toUpperCase() || 'A' }}
        </div>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-semibold text-slate-800 truncate">{{ user?.username || 'Admin' }}</p>
          <p class="text-xs text-slate-500 capitalize">{{ user?.role || 'Administrator' }}</p>
        </div>
      </div>
    </div>

    <!-- Navigation Menu -->
    <nav class="p-4 space-y-2 flex-1 overflow-y-auto max-h-[calc(100vh-280px)]">
      <a 
        *ngFor="let item of menuItems"
        [routerLink]="item.route"
        (click)="onMenuItemClick()"
        [class.bg-gradient-to-r]="isActive(item.route)"
        [class.from-green-500]="isActive(item.route)"
        [class.to-emerald-500]="isActive(item.route)"
        [class.text-white]="isActive(item.route)"
        [class.shadow-lg]="isActive(item.route)"
        class="group flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all duration-200 hover:bg-slate-50 cursor-pointer">
        
        <!-- Icon Container -->
        <div 
          [ngClass]="{
            'bg-white/20': isActive(item.route),
            'bg-slate-100': !isActive(item.route),
            'group-hover:bg-slate-200': !isActive(item.route)
          }"
          class="w-10 h-10 rounded-lg flex items-center justify-center transition-colors">
          
          <!-- Dashboard Icon -->
          <svg *ngIf="item.icon === 'dashboard'" 
               [ngClass]="{
                 'text-white': isActive(item.route),
                 'text-green-600': !isActive(item.route)
               }"
               class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
          </svg>

          <!-- Chart Icon -->
          <svg *ngIf="item.icon === 'chart'" 
               [ngClass]="{
                 'text-white': isActive(item.route),
                 'text-green-600': !isActive(item.route)
               }"
               class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
          </svg>

          <!-- Database / Knowledgebase Icon -->
          <svg *ngIf="item.icon === 'database'" 
               [ngClass]="{
                 'text-white': isActive(item.route),
                 'text-green-600': !isActive(item.route)
               }"
               class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4"></path>
          </svg>

          <!-- Help Icon -->
          <svg *ngIf="item.icon === 'help'" 
               [ngClass]="{
                 'text-white': isActive(item.route),
                 'text-green-600': !isActive(item.route)
               }"
               class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>

          <!-- Support Icon -->
          <svg *ngIf="item.icon === 'support'" 
               [ngClass]="{
                 'text-white': isActive(item.route),
                 'text-green-600': !isActive(item.route)
               }"
               class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>

          <!-- Chat Icon -->
          <svg *ngIf="item.icon === 'chat'" 
               [ngClass]="{
                 'text-white': isActive(item.route),
                 'text-green-600': !isActive(item.route)
               }"
               class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"></path>
          </svg>

          <!-- Settings Icon -->
          <svg *ngIf="item.icon === 'settings'" 
               [ngClass]="{
                 'text-white': isActive(item.route),
                 'text-green-600': !isActive(item.route)
               }"
               class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
          </svg>
        </div>

        <!-- Label -->
        <div class="flex-1 min-w-0">
          <p 
            [ngClass]="{
              'text-white': isActive(item.route),
              'text-slate-700': !isActive(item.route)
            }"
            class="text-sm font-semibold truncate">
            {{ item.label }}
          </p>
          <p 
            [ngClass]="{
              'text-green-100': isActive(item.route),
              'text-slate-500': !isActive(item.route)
            }"
            class="text-xs truncate">
            {{ item.description }}
          </p>
        </div>

        <!-- Active Indicator -->
        <div *ngIf="isActive(item.route)" class="w-1.5 h-8 bg-white rounded-full"></div>
      </a>
    </nav>

    <!-- Logout Button -->
    <div class="p-4 border-t border-slate-200">
      <button 
        (click)="logout()"
        class="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl transition-colors">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
        </svg>
        <span class="font-semibold">Logout</span>
      </button>
    </div>
  </aside>

  <!-- Main Content Area -->
  <div class="flex-1 flex flex-col min-h-screen">
    
    <!-- Top Navigation Bar -->
    <header class="sticky top-0 z-40 backdrop-blur-md bg-white/30 border-b border-white/20">
      <div class="h-20 px-6 flex items-center justify-between">
        
        <!-- Left Section -->
        <div class="flex items-center gap-4">
          <!-- Mobile Menu Toggle -->
          <button 
            (click)="toggleSidebar()"
            class="lg:hidden p-2 hover:bg-white/20 rounded-lg transition-colors">
            <svg class="w-6 h-6 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"></path>
            </svg>
          </button>

          <!-- Back Button -->
          <button 
            (click)="goBack()"
            class="hidden sm:flex items-center gap-2 px-4 py-2 text-slate-700 hover:bg-white/20 rounded-lg transition-colors">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
            </svg>
            <span class="font-medium">Back</span>
          </button>

          <!-- Page Title -->
          <div class="hidden md:block">
            <h1 class="text-xl font-bold text-slate-800">{{ getPageTitle() }}</h1>
            <p class="text-sm text-slate-600">Mental Health Chatbot System</p>
          </div>
        </div>

        <!-- Right Section -->
        <div class="flex items-center gap-3">
          <!-- Live Indicator -->
          <div class="hidden sm:flex items-center gap-2 px-3 py-2 bg-emerald-500/20 text-emerald-700 rounded-lg backdrop-blur-sm">
            <div class="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
            <span class="text-xs font-medium">Live</span>
          </div>

          <!-- Notifications -->
          <button class="relative p-2 hover:bg-white/20 rounded-lg transition-colors">
            <svg class="w-6 h-6 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
            </svg>
            <span class="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
          </button>

          <!-- User Avatar -->
          <div class="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold">
            {{ user?.username?.charAt(0).toUpperCase() || 'A' }}
          </div>
        </div>
      </div>
    </header>

    <!-- Page Content -->
    <main class="flex-1 overflow-auto">
      <router-outlet></router-outlet>
    </main>
  </div>

  <!-- Sidebar Overlay (Mobile) -->
  <div 
    *ngIf="sidebarOpen"
    (click)="toggleSidebar()"
    class="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm transition-opacity"></div>
</div>
